import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import CustomHeader from '../components/CustomHeader';
import opLinuxImg from '../op_Linux_img.png';
import serCarepackImg from '../ser_carepack_img.jpg';
import '../App.css';
import { isMobileDevice } from '../utils/deviceDetection';

const SupportPage = () => {
  const location = useLocation();
  const [activeCategory, setActiveCategory] = useState('carepack');
  const [isMobile, setIsMobile] = useState(isMobileDevice());
  
  // 각 섹션에 대한 ref 생성
  const carepackRef = useRef(null);
  const dbmsConsultingRef = useRef(null);

  const categories = [
    { id: 'carepack', name: '케어팩서비스', title: '케어팩 서비스' },
    { id: 'dbms-consulting', name: 'DBMS컨설팅', title: 'DBMS 컨설팅 서비스' }
  ];

  const refs = {
    carepack: carepackRef,
    'dbms-consulting': dbmsConsultingRef
  };

  // URL 파라미터 확인하여 초기 카테고리 설정
  useEffect(() => {
    // 페이지 로드 시 스크롤을 맨 위로 이동
    window.scrollTo(0, 0);
    
    const params = new URLSearchParams(location.search);
    const category = params.get('category');
    if (category && categories.find(cat => cat.id === category)) {
      setActiveCategory(category);
      // 페이지 로드 후 해당 섹션으로 스크롤
      setTimeout(() => {
        const targetRef = refs[category];
        if (targetRef && targetRef.current) {
          const headerOffset = 88;
          const elementPosition = targetRef.current.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  }, [location.search]);

  // Intersection Observer 설정
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '-20% 0px -70% 0px', // 상단 20%, 하단 70% 위치에서 트리거
      threshold: 0
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          // 현재 보이는 섹션의 카테고리 ID 찾기
          const categoryId = Object.keys(refs).find(key => 
            refs[key].current === entry.target
          );
          if (categoryId) {
            setActiveCategory(categoryId);
          }
        }
      });
    }, observerOptions);

    // 모든 섹션을 관찰 대상으로 등록
    Object.values(refs).forEach(ref => {
      if (ref.current) {
        observer.observe(ref.current);
      }
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(isMobileDevice());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleCategoryClick = (categoryId) => {
    setActiveCategory(categoryId);
    const targetRef = refs[categoryId];
    if (targetRef && targetRef.current) {
      // 상단 고정 헤더 높이 (68px) + 여유 공간 (20px) = 88px
      const headerOffset = 88;
      const elementPosition = targetRef.current.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className={`subcustom-header-container${isMobile ? ' mobile-device' : ''}`}>
      <CustomHeader />
      <header className="subcustom-header">
        <div className="hero-title-area">
          <div className="hero-subtitle">TECHNICAL SERVICES</div>
          <h2 className="hero-title">기술서비스</h2>
        </div>
      </header>

      <main className="infra-main">
        <div className="solutions-layout">
          {/* 왼쪽 사이드바 카테고리 메뉴 */}
          <div className="sidebar-categories">
            <h3 className="sidebar-title">기술서비스</h3>
            <ul className="category-menu">
              {categories.map(category => (
                <li key={category.id}>
                  <button
                    className={`category-item ${activeCategory === category.id ? 'active' : ''}`}
                    onClick={() => handleCategoryClick(category.id)}
                  >
                    {category.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* 오른쪽 콘텐츠 영역 */}
          <div className="content-area">
            {/* 케어팩 서비스 섹션 */}
            <div className="solution-section carepack-section" ref={carepackRef}>
              <div className="carepack-header">
                <div className="carepack-icon">
                  <div className="shield-icon">
                    <i className="shield-symbol"></i>
                  </div>
                </div>
                <h2 className="section-title">케어팩 서비스</h2>
                <p className="carepack-subtitle">COMPREHENSIVE IT SUPPORT & MAINTENANCE</p>
              </div>
              
              <div className="carepack-main-content">
                <div className="carepack-left-section">
                  <div className="carepack-intro">
                    <div className="intro-badge">20년+ 전문 경험</div>
                    <p className="highlight-text">20년 이상의 풍부한 경험을 통한 기술력을 바탕으로 오직 고객중심의 IT서비스 환경에 적합하도록 맞춤형 서비스를<br/> 제공해 드립니다.</p>
                  </div>
                </div>
                
                <div className="carepack-right-section">
                  <div className="image-container">
                    <img src={serCarepackImg} alt="케어팩 서비스" />
                  </div>
                </div>
              </div>

              {/* 서비스 상세 테이블 - 하단에 가로로 배치 */}
              <div className="carepack-table-container">
                <table className="carepack-table">
                  <thead>
                    <tr>
                      <th>지원내용</th>
                      <th>상세 서비스</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="service-category">24x7x365 기술지원 서비스</td>
                      <td>온사이트/원격지원/유선/이메일 서비스 등</td>
                    </tr>
                    <tr>
                      <td className="service-category">서비스 내용</td>
                      <td>설치 및 구성/업그레이드/튜닝/장애 지원/정기 점검/보안취약점 점검 등</td>
                    </tr>
                    <tr>
                      <td className="service-category">부가서비스</td>
                      <td>애플리케이션 성능분석/모니터링/매뉴얼 제공 Migration 서비스</td>
                    </tr>
                    <tr>
                      <td className="service-category">지원 대상</td>
                      <td>
                        <div className="support-categories">
                          <div className="support-category">
                            <h5>운영체제</h5>
                            <div className="category-items">
                              <span className="support-item">RHEL</span>
                              <span className="support-item">Rocky Linux</span>
                              <span className="support-item">Ubuntu LTS</span>
                              <span className="support-item">Oracle Linux</span>
                            </div>
                          </div>
                          <div className="support-category">
                            <h5>웹서버</h5>
                            <div className="category-items">
                              <span className="support-item">Apache</span>
                              <span className="support-item">NginX</span>
                            </div>
                          </div>
                          <div className="support-category">
                            <h5>WAS</h5>
                            <div className="category-items">
                              <span className="support-item">Tomcat</span>
                              <span className="support-item">Wildfly</span>
                              <span className="support-item">Jboss EAP</span>
                              <span className="support-item">Weblogic JEUS</span>
                              <span className="support-item">Resin</span>
                              <span className="support-item">LENA</span>
                            </div>
                          </div>
                          <div className="support-category">
                            <h5>데이터베이스</h5>
                            <div className="category-items">
                              <span className="support-item">PostgreSQL</span>
                              <span className="support-item">MariaDBMS</span>
                              <span className="support-item">Oracle</span>
                              <span className="support-item">Tibero</span>
                              <span className="support-item">DB2</span>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* DBMS 컨설팅 서비스 섹션 */}
            <div className="solution-section carepack-section" ref={dbmsConsultingRef}>
              <h2 className="section-title">DBMS 컨설팅 서비스</h2>
              
              {/* 주요 내용 섹션 */}
              <div className="consulting-key-contents">
                <div className="consulting-table-container">
                  <table className="consulting-table">
                    <thead>
                      <tr>
                        <th>구분</th>
                        <th>상세 내용</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="service-category">목적</td>
                        <td>DBMS 시스템 및 애플리케이션의 성능 최적화와 운영 시스템 안정화</td>
                      </tr>
                      <tr>
                        <td className="service-category">서비스 내용</td>
                        <td>
                          <div className="service-list">
                            <div className="service-item"><span className="service-number">1</span>DB 설계</div>
                            <div className="service-item"><span className="service-number">2</span>DBMS 시스템 진단</div>
                            <div className="service-item"><span className="service-number">3</span>애플리케이션 성능진단(SQL 등)</div>
                            <div className="service-item"><span className="service-number">4</span>시스템 튜닝가이드</div>
                            <div className="service-item"><span className="service-number">5</span>SQL 튜닝 가이드</div>
                          </div>
                          <span className="note">* 위 내용 중 일부 선택이 가능하고 각 단계별 보고서 제공</span>
                        </td>
                      </tr>
                      <tr>
                        <td className="service-category">지원 대상</td>
                        <td>PostgreSQL, MariaDBMS, MySQL, DB2, Oracle</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* 컨설팅 절차 섹션 */}
              <div className="consulting-procedure">
                <h3 className="procedure-title">컨설팅 절차</h3>
                
                <div className="procedure-flow">
                  <div className="flow-row">
                    <div className="flow-stage">
                      <div className="stage-header">
                        <h4>1단계</h4>
                        <h5>착수단계</h5>
                        <span className="stage-subtitle">(ERD 작성)</span>
                      </div>
                      <ul className="stage-tasks">
                        <li>DB운영데이터 수집</li>
                        <li>사전 미팅, 현황 파악</li>
                        <li>현 이슈 자료수집</li>
                        <li>PJT 상세 스케줄 정의</li>
                      </ul>
                    </div>
                    
                    <div className="flow-arrow">→</div>
                    
                    <div className="flow-stage">
                      <div className="stage-header">
                        <h4>2단계</h4>
                        <h5>분석단계</h5>
                        <span className="stage-subtitle">(DB/SQL점검)</span>
                      </div>
                      <ul className="stage-tasks">
                        <li>현 DB상태 및 SQL문</li>
                        <li>품질 및 성능 점검</li>
                        <li>튜닝대상(SQL선정)</li>
                        <li>장애유발 요인 분석</li>
                      </ul>
                    </div>
                  </div>
                  
                  <div className="flow-row">
                    <div className="flow-stage">
                      <div className="stage-header">
                        <h4>3단계</h4>
                        <h5>수행단계</h5>
                        <span className="stage-subtitle">(튜닝대상 프로그램)</span>
                      </div>
                      <ul className="stage-tasks">
                        <li>SQL 성능튜닝 제안</li>
                        <li>DB성능이슈 해결</li>
                        <li>개선한 적용 및 효과</li>
                        <li>분석 및 테스트</li>
                      </ul>
                    </div>
                    
                    <div className="flow-arrow">→</div>
                    
                    <div className="flow-stage">
                      <div className="stage-header">
                        <h4>4단계</h4>
                        <h5>평가.종료</h5>
                        <span className="stage-subtitle">(결과보고서 작성)</span>
                      </div>
                      <ul className="stage-tasks">
                        <li>SQL, 시스템 튜닝가이드 작성</li>
                        <li>개선 효과 결과 보고</li>
                        <li>PJT 상세 스케줄 정의</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SupportPage; 